<template>
  <article class="order-receipt">
    <div class="order-receipt__container">
      <header>
        <i18n-t keypath="title.select_receipt" tag="h1">
          <template #receipt>
            <span>{{ t('title.receipt') }}</span>
          </template>
        </i18n-t>
      </header>
      <main>
        <button
          v-if="configStore.useElectronicReceipt"
          type="button"
          class="order-receipt__button"
          @mouseup="handleElectronicReceiptClick"
          @touchend.prevent="handleElectronicReceiptClick"
        >
          <span class="order-receipt__figure">
            <img src="@/assets/images/img-receipt-phone.png" alt="전자 영수증 이미지" />
          </span>
          <span class="order-receipt__text">{{ t('button.e_receipt') }}</span>
        </button>
        <button
          v-if="configStore.config.IsPaperReceiptUsed"
          type="button"
          class="order-receipt__button"
          @mouseup="handlePaperReceiptClick"
          @touchend.prevent="handlePaperReceiptClick"
        >
          <span class="order-receipt__figure">
            <img src="@/assets/images/img-receipt-paper.png" alt="종이 영수증 이미지" />
          </span>
          <span class="order-receipt__text">{{ t('button.paper_receipt') }}</span>
        </button>
        <button
          type="button"
          class="order-receipt__button"
          @mouseup="completeReceiptSelection"
          @touchend.prevent="completeReceiptSelection"
        >
          <span class="order-receipt__figure">
            <img src="@/assets/images/img-receipt-close.png" alt="필요없음 이미지" class="small" />
          </span>
          <span class="order-receipt__text">{{ t('button.not_needed') }}</span>
        </button>
      </main>
    </div>
  </article>
</template>

<script lang="ts" setup>
  import * as Android from '@/api/android';
  import OrderComplete from '@/components/OrderComplete.vue';
  import OrderReceiptElectronic from '@/components/OrderReceiptElectronic.vue';
  import UserError from '@/error';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useModalStore } from '@/stores/modal';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{
    /**
     * 주문내역에서 영수증 요청 버튼을 통해 모달을 열었는지 구분.
     * - 주문내역에서 요청 했다면 영수증 요청 후 모달 닫기. 종이 영수증 출력시 전체 주문 출력 요청 플래그 추가.
     * - 선결제 완료 후 영수증 요청한다면 주문 완료 전체화면 모달 열기.
     */
    fromOrderHistory: boolean;
  }>();
  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const modalStore = useModalStore();
  const configStore = useConfigStore();

  /**
   * 전자영수증 선택
   * 선불 주문 유지 옵션 사용 중인 경우 경고 메시지 출력
   * 그 외에는 전자 영수증 페이지로 이동
   */
  const handleElectronicReceiptClick = () => {
    // 선불 주문 유지 옵션 사용 여부 검사
    if (configStore.config.IsPrepaidOrderKeepingUsed) throw new UserError(t('info.request_receipt_at_counter'));

    // 전자 영수증 모달 열기
    modalStore.openFullScreenModal(OrderReceiptElectronic, { fromOrderHistory: props.fromOrderHistory });
  };

  /** 종이영수증 선택 */
  const handlePaperReceiptClick = async () => {
    const loadingId = commonStore.showLoading();
    try {
      // 종이영수증 출력 요청
      const response = await Android.printReceiptAsync(props.fromOrderHistory);

      // 종이영수증 출력 요청 성공 여부 검사
      if (response.RET_CODE !== 0) throw new UserError(response.RET_MSG);
      await commonStore.showDialog({ type: 'info', message: t('info.paper_receipt_complete') });

      completeReceiptSelection();
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };

  /**
   * 영수증 선택 완료
   * - 주문내역에서 요청 했다면 모달 닫기.
   * - 선결제에서 요청 했다면 주문 완료 전체화면 모달 열기.
   */
  const completeReceiptSelection = () => {
    if (props.fromOrderHistory) {
      // 전체화면 모달 닫기
      emits('close');
    } else {
      // 주문 완료 모달 열기
      modalStore.openFullScreenModal(OrderComplete, { hasPayment: true });
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .order-receipt {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    .order-receipt__container {
      display: grid;
      grid-template-rows: auto 1fr;
      gap: 2.9851vw;
      padding: 2.9851vw;
      @media (max-width: vars.$sm) {
        gap: 40px;
        padding: 20px;
      }

      header {
        h1 {
          text-align: center;
          font-size: 3.7313vw;
          font-weight: 700;
          @media (max-width: vars.$sm) {
            font-size: 28px;
          }
          span {
            color: var(--point-color-secondary);
          }
        }
      }

      main {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        margin: 1.4925vw 0;
        overflow-y: auto;
        @media (max-width: vars.$sm) {
          flex-direction: column;
        }
        .order-receipt__button {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2.2388vw;
          width: calc(33.33%);
          height: 24.1791vw;
          background: #fbfbfb;
          border: 0.0746vw solid #e9e9e9;
          border-radius: 0.3731vw;
          @media (max-width: vars.$sm) {
            flex: 0 0 100px;
            flex-direction: row;
            gap: 10px;
            width: 100%;
            height: 100px;
          }
          .order-receipt__figure {
            img {
              width: inherit;
              height: 12.6866vw;
              @media (max-width: vars.$sm) {
                height: 50px;
              }
              &.small {
                padding: 1.1194vw 0;
                @media (max-width: vars.$sm) {
                  padding: 5px;
                }
              }
            }
          }
          .order-receipt__text {
            font-size: 2.0896vw;
            font-weight: 500;
            @media (max-width: vars.$sm) {
              font-size: 14px;
              margin-top: 12px;
            }
          }
        }
      }
    }
  }
</style>
